/**
 * Plan Pair Store
 *
 * SessionStorage wrapper for the current plan pair (primary + alternative).
 * After Phase 1C (#1413) the server stopped persisting which plan is
 * "primary"; since #1579 PR 0 it DERIVES the role per request and returns it
 * on the plans list, so this store is a cache of the pair's identity, never
 * the authority on roles — `refreshPlanPairFromServer` below is how a surface
 * reconciles the cache with the server's declared roles (#1617). Endpoints
 * that previously required server-side primary inference (`reset-to-primary`,
 * `promote-to-primary`) require both plan IDs from the caller; the
 * single-plan endpoints (save, get, delete, clone, rename) take just one
 * plan ID and don't depend on this store.
 */

import type { PlanPair } from '../types/alternativePlan';
import { getUserPlans } from './planApi';

const STORAGE_KEY = 'plan-pair';

export function savePlanPair(pair: PlanPair): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pair));
  } catch (error) {
    console.warn('Failed to save plan pair to sessionStorage:', error);
  }
}

/**
 * Validate a parsed sessionStorage value as a PlanPair.
 *
 * This checks all four fields, not just the two IDs. A pair that carries IDs
 * but no names is exactly the "stale or degenerate cached pair" that #1577
 * hardened against downstream — it is cheaper to reject it here than to guard
 * every consumer. JSON.parse returns `any`, so this is the one place the shape
 * is established rather than assumed.
 */
function isPlanPair(value: unknown): value is PlanPair {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p.primaryPlanId === 'string' && p.primaryPlanId !== '' &&
    typeof p.alternativePlanId === 'string' && p.alternativePlanId !== '' &&
    typeof p.primaryName === 'string' &&
    typeof p.alternativeName === 'string'
  );
}

export function getPlanPair(): PlanPair | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (!isPlanPair(parsed)) {
      console.warn('Plan pair in sessionStorage has invalid shape, clearing:', parsed);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const { primaryPlanId, primaryName, alternativePlanId, alternativeName } = parsed;
    return { primaryPlanId, primaryName, alternativePlanId, alternativeName };
  } catch (error) {
    console.warn('Failed to read plan pair from sessionStorage, clearing corrupted data:', error);
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clearPlanPair(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear plan pair from sessionStorage:', error);
  }
}

/**
 * True only if `pair` is a complete, self-consistent pair whose alternative
 * side is the plan actually being edited/viewed (`currentAlternativePlanId`).
 * Guards the irreversible "reset alternative to primary" / "make primary"
 * actions against a plan pair that's stale, partial, or degenerate (e.g. both
 * IDs the same) — any of which would otherwise let a copy proceed from the
 * wrong (or a nonexistent) source plan with no way to undo it.
 */
export function isPairValidForCopy(
  pair: PlanPair | null,
  currentAlternativePlanId: string | undefined,
): pair is PlanPair {
  return (
    !!pair?.primaryPlanId &&
    pair.primaryPlanId !== pair.alternativePlanId &&
    pair.alternativePlanId === currentAlternativePlanId
  );
}

export function swapPrimaryAndAlternative(): PlanPair | null {
  const pair = getPlanPair();
  if (!pair) return null;

  const swapped: PlanPair = {
    primaryPlanId: pair.alternativePlanId,
    primaryName: pair.alternativeName,
    alternativePlanId: pair.primaryPlanId,
    alternativeName: pair.primaryName,
  };

  savePlanPair(swapped);
  return swapped;
}

/**
 * Reconcile the cached pair with the server's declared roles (#1617).
 *
 * - Exactly two plans with valid roles → the pair is rebuilt from the
 *   server's `role` field, cached, and returned. The cache and the URL are
 *   never the authority; the server is.
 * - Fewer/more than two plans → any cached pair is stale (a plan was
 *   deleted); the cache is cleared and null returned.
 * - Roles missing or contradictory (an old server during a deploy, or a
 *   server bug) → NO claim is made: the cache is left untouched and returned
 *   as-is, with a warning. Guessing here is the #1576 bug class.
 *
 * Callers own the fetch failure: this throws if the list request fails.
 */
export async function refreshPlanPairFromServer(): Promise<PlanPair | null> {
  const { plans } = await getUserPlans();
  if (plans.length !== 2) {
    clearPlanPair();
    return null;
  }
  const primary = plans.find(p => p.role === 'primary');
  const alt = plans.find(p => p.role === 'alternative');
  if (!primary || !alt) {
    console.warn(
      `Plan pair roles unavailable from server (roles: [${plans.map(p => p.role ?? 'missing').join(', ')}]) — keeping the cached pair unverified.`,
    );
    return getPlanPair();
  }
  const pair: PlanPair = {
    primaryPlanId: primary.planId,
    primaryName: primary.name,
    alternativePlanId: alt.planId,
    alternativeName: alt.name,
  };
  savePlanPair(pair);
  return pair;
}
